// Lightweight, dependency-free activity & section-timing tracker for the payment plugin.
//
// Records every meaningful event in real-time with timestamps, and auto-computes how
// long the user spends in each section (dwell time) so the merchant can run statistics
// on drop-off, method preference, time-to-pay, etc.
//
// Sink strategy (best practice for a payment page):
//   • Every event is dispatched IMMEDIATELY (real-time) through a single dispatch() call.
//   • Today that sink = console + sessionStorage (inspect live, nothing can break).
//   • Set ANALYTICS_ENDPOINT below once the backend provides a URL — each event then also
//     POSTs via navigator.sendBeacon / fetch(keepalive) with zero changes at any call site.
//   • A safety-net flush() fires on page hide/unload so abandoned sessions are still captured.
//
// PRIVACY: never log raw PII (mobile, email, UPI ID, card). Log only booleans, lengths,
// method/app names, validation results and durations.

import GetAPI from "./GetAPI";

// Relative path, proxied by nginx to the backend (same convention as all other APIs).
const ANALYTICS_ENDPOINT = GetAPI("activity"); // "/api/v2/plugin/activity"
const STORAGE_KEY = "swinkpay_activity";

const now = () => Date.now();

const state = {
  started: false,
  sessionStart: 0,
  lastEventTs: 0,
  context: {},
  currentSection: null,
  sectionEnterTs: 0,
  sectionTotals: {}, // name -> { ms, visits }
  events: [],
  seq: 0,
  outcome: null, // paid | failed | abandoned | timeout | null(=pending)
};

// Never let analytics break the payment flow.
const safe = (fn) => { try { return fn(); } catch (e) { /* swallow */ } };

// Merge context WITHOUT letting incomplete/late payloads clobber good values.
// A redirect-return re-init (getURLDatafromToken) often carries partial data (missing
// txnId, NaN pg), which must not wipe the resumed identity. Skips undefined/null/NaN,
// and treats identity fields as write-once; enrichment fields (swinkpayId) still update.
const IDENTITY_KEYS = ["txnId", "mid", "tid", "pg", "amount"];
function mergeContext(base, incoming) {
  const out = { ...base };
  Object.keys(incoming || {}).forEach((k) => {
    const v = incoming[k];
    if (v === undefined || v === null) return;
    if (typeof v === "number" && Number.isNaN(v)) return;
    if (IDENTITY_KEYS.includes(k) && out[k] !== undefined && out[k] !== null && out[k] !== "") return;
    out[k] = v;
  });
  return out;
}

const persist = () =>
  safe(() => sessionStorage.setItem(STORAGE_KEY, JSON.stringify(getSummary())));

// MODE controls what hits the network:
//   "summary"  → only session summaries are POSTed (at checkpoints + page exit). Recommended.
//   "realtime" → every individual event is also POSTed as it happens.
const MODE = "summary";

// Local recording only (console) — never touches the network.
const logLocal = (payload) =>
  safe(() => console.debug("[activity]", payload.event || payload.type, payload));

// Send one payload to the backend. Uses sendBeacon (survives page unload) when asked,
// else fetch(keepalive). No-op until ANALYTICS_ENDPOINT is set.
const sendNetwork = (payload, beacon = false) => {
  if (!ANALYTICS_ENDPOINT) return;
  safe(() => {
    const body = JSON.stringify(payload);
    if (beacon && navigator.sendBeacon) {
      navigator.sendBeacon(ANALYTICS_ENDPOINT, new Blob([body], { type: "application/json" }));
    } else {
      fetch(ANALYTICS_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      });
    }
  });
};

let listenersAttached = false;

function attachFlushListeners() {
  if (listenersAttached) return;
  listenersAttached = true;
  // Safety-net flush so abandoned / redirected sessions are still recorded.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush("page_hidden");
  });
  window.addEventListener("pagehide", () => flush("page_hide"));
}

function startFresh(context) {
  state.sessionStart = now();
  state.lastEventTs = state.sessionStart;
  state.currentSection = null;
  state.sectionEnterTs = 0;
  state.sectionTotals = {};
  state.events = [];
  state.seq = 0;
  state.outcome = null;
  // Capture an automation hint once (headless/bot browsers expose navigator.webdriver).
  state.context = mergeContext({ automation: !!(typeof navigator !== "undefined" && navigator.webdriver) }, context);
  track("session_start");
}

// Resume a mid-flight session across a redirect round-trip (bank/PG). sessionStorage
// survives same-tab navigation, so a netbanking/card return continues the SAME session
// (same seq sequence, same txnId) instead of starting a fragmented new one.
function tryResume(context) {
  const saved = safe(() => JSON.parse(sessionStorage.getItem(STORAGE_KEY)));
  if (!saved || !Array.isArray(saved.events) || saved.events.length === 0) return false;
  if (saved.outcome === "paid" || saved.outcome === "failed") return false; // already terminal
  if (context.txnId && saved.txnId && context.txnId !== saved.txnId) return false; // different checkout
  state.events = saved.events;
  state.seq = saved.events.reduce((m, e) => Math.max(m, e.seq || 0), 0);
  state.sectionTotals = {};
  Object.keys(saved.sectionTotals || {}).forEach((k) => {
    state.sectionTotals[k] = { ms: saved.sectionTotals[k].ms || 0, visits: saved.sectionTotals[k].visits || 1 };
  });
  state.outcome = saved.outcome ?? null;
  state.currentSection = null; // prior live section already folded into sectionTotals
  state.sectionEnterTs = 0;
  state.sessionStart = now() - (saved.sessionDurationMs || 0); // keep elapsed time continuous
  state.lastEventTs = now();
  // eslint-disable-next-line no-unused-vars
  const { sessionDurationMs, currentSection, eventCount, events, sectionTotals, risk, outcome, ...ctx } = saved;
  state.context = mergeContext(ctx, context);
  track("session_resumed");
  return true;
}

export function init(context = {}) {
  return safe(() => {
    if (state.started) {
      // A different checkout in the same tab → restart fresh; otherwise just merge IDs.
      if (context.txnId && state.context.txnId && context.txnId !== state.context.txnId) {
        startFresh(context);
      } else {
        state.context = mergeContext(state.context, context);
      }
      return;
    }
    state.started = true;
    if (!tryResume(context)) startFresh(context);
    attachFlushListeners();
  });
}

export function track(event, props = {}) {
  return safe(() => {
    if (!state.started) return;
    const ts = now();
    const rec = {
      seq: ++state.seq,
      event,
      section: state.currentSection,
      t: ts - state.sessionStart, // ms since session start
      dt: ts - state.lastEventTs, // ms since previous event
      // event-specific extras go in a nested props object (app, field, method, dwellMs, ...)
      props: props && Object.keys(props).length ? props : null,
    };
    state.lastEventTs = ts;
    state.events.push(rec);

    // Derive the terminal outcome so the backend can store it directly (no event parsing).
    if (event === "transaction_completed") {
      state.outcome = props?.statusCode === 1 ? "paid"
                    : props?.statusCode === 2 ? "failed"
                    : "pending";
    } else if (event === "payment_abandoned") {
      state.outcome = "abandoned";
    } else if (event === "session_timeout") {
      state.outcome = "timeout";
    }

    logLocal(rec);
    if (MODE === "realtime") sendNetwork({ type: "event", ...state.context, ...rec });
    persist();
  });
}

// Enter a section. Automatically closes the previous section and accrues its dwell time.
export function enterSection(name) {
  return safe(() => {
    if (!state.started || state.currentSection === name) return;
    exitSection();
    state.currentSection = name;
    state.sectionEnterTs = now();
    track("section_enter", { sectionName: name });
  });
}

export function exitSection() {
  return safe(() => {
    if (!state.started || !state.currentSection) return;
    const prev = state.currentSection;
    const ms = now() - state.sectionEnterTs;
    const agg = state.sectionTotals[prev] || { ms: 0, visits: 0 };
    agg.ms += ms;
    agg.visits += 1;
    state.sectionTotals[prev] = agg;
    track("section_exit", { sectionName: prev, dwellMs: ms }); // section still = prev here
    state.currentSection = null;
  });
}

// Derive a fraud-risk summary from the event log. Counts are authoritative facts;
// the score/level is a CONVENIENCE baseline — the backend should compute the final,
// authoritative score (it can't be tampered there and can add IP/device/history).
// Weights are intentionally simple and tunable.
function computeRisk() {
  const evs = state.events;
  const count = (name) => evs.filter((e) => e.event === name).length;
  const maxAttempt = (name) =>
    evs.filter((e) => e.event === name).reduce((m, e) => Math.max(m, e.props?.attemptNo || 0), 0);

  const distinctCards = maxAttempt("card_attempt");
  const distinctVpas = maxAttempt("upi_attempt");
  const distinctBanks = maxAttempt("bank_selected");
  const distinctWallets = maxAttempt("wallet_selected");
  const validationFailures = count("contact_validation_blocked") + count("upi_qr_failed");
  const backAttempts = count("back_button_attempt");
  const submitCount = count("payment_submit");
  const timeouts = count("session_timeout");
  const cardPaste = count("card_paste") > 0;
  const abandoned = count("payment_abandoned") > 0;
  const automation = !!state.context.automation;
  const durationMs = state.started ? now() - state.sessionStart : 0;
  const submitEv = evs.find((e) => e.event === "payment_submit");
  const fastSubmit = submitEv ? submitEv.t < 4000 : false; // paid within 4s of landing = bot-like
  // Payment method actually chosen this session (CARD/UPI/NETBANKING/WALLET/QRCODE).
  // Backend promotes signals.method → indexed column fldmethod, so it must be present here.
  // Take the most recent method-bearing event (submit/initiated/method_selected); null if browse-only.
  const method =
    [...evs].reverse().find((e) => e.props && e.props.method)?.props.method || null;

  let score = 0;
  const flags = [];
  if (distinctCards >= 3) { score += 45; flags.push("card_testing"); }
  else if (distinctCards === 2) { score += 18; }
  if (distinctVpas >= 3) { score += 38; flags.push("vpa_enumeration"); }
  else if (distinctVpas === 2) { score += 14; }
  if (distinctBanks >= 4 || distinctWallets >= 4) { score += 8; flags.push("instrument_hopping"); }
  if (automation) { score += 30; flags.push("automation"); }
  if (fastSubmit) { score += 20; flags.push("velocity"); }
  if (cardPaste) { score += 12; flags.push("card_paste"); }
  if (validationFailures >= 3) { score += 10; flags.push("repeated_validation_failure"); }
  if (submitCount >= 3) { score += 12; flags.push("repeated_submit"); }
  if (backAttempts >= 2) { score += 5; }
  if (timeouts >= 1) { score += 3; }

  score = Math.min(100, score);
  const level = score >= 61 ? "high" : score >= 31 ? "medium" : "low";

  return {
    score,
    level,
    flags,
    signals: {
      method,
      distinctCards, distinctVpas, distinctBanks, distinctWallets,
      validationFailures, backAttempts, submitCount, timeouts,
      automation, fastSubmit, cardPaste, abandoned, durationMs,
    },
  };
}

// Full session snapshot, including the live section's accrued time (without closing it).
export function getSummary() {
  const totals = {};
  Object.keys(state.sectionTotals).forEach((k) => { totals[k] = { ...state.sectionTotals[k] }; });
  if (state.currentSection) {
    const liveMs = now() - state.sectionEnterTs;
    const cur = totals[state.currentSection] || { ms: 0, visits: 0 };
    totals[state.currentSection] = { ms: cur.ms + liveMs, visits: cur.visits || 1 };
  }
  return {
    ...state.context,
    sessionDurationMs: state.started ? now() - state.sessionStart : 0,
    currentSection: state.currentSection,
    outcome: state.outcome, // paid | failed | abandoned | timeout | null(=pending)
    risk: computeRisk(),    // { score, level, flags[], signals{} } — see computeRisk()
    sectionTotals: totals,
    eventCount: state.events.length,
    events: state.events,
  };
}

// Send the full cumulative session summary to the backend. Called at checkpoints
// (contact done, method chosen, app tapped, payment submitted) and on page exit,
// so a closed/crashed window loses at most the last few seconds. Each call carries
// the complete snapshot; the backend upserts on txn_id, so resends never duplicate.
export function flush(reason = "manual") {
  return safe(() => {
    if (!state.started) return;
    const summary = { type: "session_summary", flushReason: reason, ...getSummary() };
    logLocal(summary);
    sendNetwork(summary, true);
    persist();
  });
}

const analytics = { init, track, enterSection, exitSection, getSummary, flush };
export default analytics;
